import React from 'react';
import { BlockButtonStyle, BlockStyle, LandingBlock, MobileBlockStyle } from '../../types';
import { BUTTON_ICONS, FONT_WEIGHTS } from '../../data/fonts';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Switch } from '../ui/Switch';
interface StyleSettingsProps {
  block: LandingBlock;
  onChange: (block: LandingBlock) => void;
}
const DEFAULT_STYLE: BlockStyle = {
  background: 'transparente',
  align: 'center',
  shadow: 'ninguna',
  maxWidth: 'normal'
};

/** Deslizador con valor numérico visible. */
function Slider({
  id,
  label,
  min,
  max,
  step = 1,
  suffix = 'px',
  value,
  onChange,
  onReset
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  value: number | undefined;
  onChange: (value: number) => void;
  /** Vuelve al valor predeterminado del bloque (sin sobrescribir). */
  onReset?: () => void;
}) {
  return <div data-id="element-433" data-mp="1cgdonu8lgqbl">
      <div className="mb-1.5 flex items-center justify-between gap-2" data-id="element-434" data-mp="1ch5c2hs1fhqs">
        <label htmlFor={id} className="text-sm font-medium text-foreground" data-id="element-435" data-mp="2spj2rzgzumi5">
          {label}
        </label>
        {onReset && value !== undefined && <button type="button" onClick={onReset} className="text-[11px] font-medium text-primary underline-offset-2 hover:underline" data-id="element-436" data-mp="2nnbmwyt1mmaj">
            Restaurar
          </button>}
      </div>
      <div className="flex items-center gap-3" data-id="element-437" data-mp="2xm9hojh2bc1z">
        <input id={id} type="range" min={min} max={max} step={step} value={value ?? min} onChange={e => onChange(Number(e.target.value))} className="h-1.5 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary" data-id="element-438" data-mp="3befupwox52x7" />
        {/* Campo numérico: permite escribir un valor exacto (31, 32, 33…). */}
        <input aria-label={`${label} (valor exacto)`} type="number" min={min} max={max} step={step} value={value === undefined ? '' : value} placeholder="Auto" onChange={e => {
        const raw = e.target.value;
        if (raw === '') {
          onReset?.();
          return;
        }
        onChange(Number(raw));
      }} className="h-9 w-[74px] flex-none rounded-lg border border-border bg-background px-2 text-right text-sm tabular-nums text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-439" data-mp="3gf3j0alrj5nq" />
        <span className="flex-none text-xs text-muted-foreground" data-id="element-440" data-mp="3v5bheneralhz">{suffix}</span>
      </div>
    </div>;
}

/** Editor visual de estilo, tipografía y botón de un bloque. */
export function StyleSettings({
  block,
  onChange
}: StyleSettingsProps) {
  const style: BlockStyle = {
    ...DEFAULT_STYLE,
    ...(block.style ?? {})
  };
  const button: BlockButtonStyle = block.button ?? {};
  const mobile: MobileBlockStyle = style.mobile ?? {
    sync: true
  };
  const tablet: MobileBlockStyle = style.tablet ?? {
    sync: true
  };
  function setDevice(key: 'mobile' | 'tablet', partial: Partial<MobileBlockStyle>) {
    const current = key === 'mobile' ? mobile : tablet;
    onChange({
      ...block,
      style: {
        ...style,
        [key]: {
          ...current,
          ...partial
        }
      }
    });
  }
  function setMobile(partial: Partial<MobileBlockStyle>) {
    setDevice('mobile', partial);
  }
  function setStyle(partial: Partial<BlockStyle>) {
    onChange({
      ...block,
      style: {
        ...style,
        ...partial
      }
    });
  }
  function setButton(partial: Partial<BlockButtonStyle>) {
    onChange({
      ...block,
      button: {
        ...button,
        ...partial
      }
    });
  }
  return <div className="space-y-6" data-id="element-441" data-mp="1kgeq27xtq0fy">
      <section className="space-y-4" data-id="element-442" data-mp="1khgah30qbzac">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-443" data-mp="1fppw8lyo8rdn">
          Fondo y caja
        </h4>
        <Select id={`${block.id}-bg`} label="Fondo" options={[{
        value: 'transparente',
        label: 'Transparente'
      }, {
        value: 'blanco',
        label: 'Blanco'
      }, {
        value: 'gris',
        label: 'Gris claro'
      }, {
        value: 'marca',
        label: 'Color de marca'
      }, {
        value: 'oscuro',
        label: 'Oscuro'
      }]} value={style.background ?? 'transparente'} onChange={e => setStyle({
        background: e.target.value as BlockStyle['background']
      })} data-id="element-444" data-mp="1bnbfc7ekc1p0" />
        <Slider id={`${block.id}-py`} label="Espaciado vertical" min={0} max={160} step={4} value={style.paddingY} onChange={paddingY => setStyle({
        paddingY
      })} data-id="element-445" data-mp="1eynqbru8gzw2" />
        <Slider id={`${block.id}-px`} label="Espaciado horizontal" min={0} max={120} step={4} value={style.paddingX} onChange={paddingX => setStyle({
        paddingX
      })} data-id="element-446" data-mp="26pbp4x8fpt6k" />
        <Slider id={`${block.id}-radius`} label="Radio de bordes" min={0} max={48} value={style.radius} onChange={radius => setStyle({
        radius
      })} data-id="element-447" data-mp="1xwi74s1lsfp0" />
        <Select id={`${block.id}-shadow`} label="Sombra" options={[{
        value: 'ninguna',
        label: 'Sin sombra'
      }, {
        value: 'suave',
        label: 'Suave'
      }, {
        value: 'media',
        label: 'Media'
      }, {
        value: 'fuerte',
        label: 'Fuerte'
      }]} value={style.shadow ?? 'ninguna'} onChange={e => setStyle({
        shadow: e.target.value as BlockStyle['shadow']
      })} data-id="element-448" data-mp="21uxreo6av9yq" />
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-449" data-mp="sndz8onfs08c">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-450" data-mp="npm9a06cqo6s">
          Tipografía
        </h4>
        <Select id={`${block.id}-align`} label="Alineación" options={[{
        value: 'left',
        label: 'Izquierda'
      }, {
        value: 'center',
        label: 'Centro'
      }, {
        value: 'right',
        label: 'Derecha'
      }]} value={style.align ?? 'center'} onChange={e => setStyle({
        align: e.target.value as BlockStyle['align']
      })} data-id="element-451" data-mp="t6pkv85i37" />
        <Select id={`${block.id}-weight`} label="Grosor" placeholder="Automático" options={FONT_WEIGHTS} value={style.titleWeight ? String(style.titleWeight) : ''} onChange={e => setStyle({
        titleWeight: e.target.value ? Number(e.target.value) : undefined
      })} data-id="element-452" data-mp="1lcl8oiofia8d" />
        {/* Tamaños de escritorio: independientes de tablet y móvil. */}
        <Slider id={`${block.id}-d-title`} label="Tamaño del título" min={8} max={96} value={style.titleSize} onReset={() => setStyle({
        titleSize: undefined
      })} onChange={titleSize => setStyle({
        titleSize
      })} data-id="element-453" data-mp="1seq5wa4uynla" />
        <Slider id={`${block.id}-d-body`} label="Tamaño del texto" min={8} max={64} value={style.bodySize} onReset={() => setStyle({
        bodySize: undefined
      })} onChange={bodySize => setStyle({
        bodySize
      })} data-id="element-454" data-mp="14qqb8z4fed1f" />
        <Slider id={`${block.id}-tracking`} label="Espaciado entre letras" min={-2} max={8} step={0.5} value={style.letterSpacing} onChange={letterSpacing => setStyle({
        letterSpacing
      })} data-id="element-455" data-mp="1env2hdd4oyp3" />
        <Input id={`${block.id}-color`} label="Color del texto" type="color" value={style.textColor ?? '#0B0B0F'} onChange={e => setStyle({
        textColor: e.target.value
      })} data-id="element-456" data-mp="lz60zd85urct" />
        <Switch id={`${block.id}-upper`} label="Texto en mayúsculas" checked={Boolean(style.uppercase)} onChange={uppercase => setStyle({
        uppercase
      })} data-id="element-457" data-mp="349v681hvrq6y" />
      </section>

      {/* Ajustes que solo afectan a tablet (641–1024 px). */}
      <section className="space-y-4 border-t border-border pt-5" data-id="element-458" data-mp="2vazz8n70ic2l">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-459" data-mp="2q90tj6nsv32h">
          Tablet
        </h4>
        <Switch id={`${block.id}-t-sync`} label="Usar los mismos valores en todos los dispositivos" checked={tablet.sync !== false} onChange={sync => setDevice('tablet', {
        sync
      })} data-id="element-460" data-mp="2lc0p106lm8dh" />
        {tablet.sync === false && <div className="space-y-4 rounded-xl bg-surface p-4" data-id="element-461" data-mp="2cg1iphyypx19">
            <p className="text-xs text-muted-foreground" data-id="element-462" data-mp="27kvx6k9h2kdp">
              Estos valores sustituyen solo la versión de tablet. Escritorio y
              móvil no cambian.
            </p>
            <Slider id={`${block.id}-t-title`} label="Tamaño del título" min={8} max={96} onReset={() => setDevice('tablet', {
          titleSize: undefined
        })} value={tablet.titleSize} onChange={titleSize => setDevice('tablet', {
          titleSize
        })} data-id="element-463" data-mp="1xmg2k7b8kszg" />
            <Slider id={`${block.id}-t-body`} label="Tamaño del texto" min={8} max={64} onReset={() => setDevice('tablet', {
          bodySize: undefined
        })} value={tablet.bodySize} onChange={bodySize => setDevice('tablet', {
          bodySize
        })} data-id="element-464" data-mp="3cho4yf0u90qk" />
            <Slider id={`${block.id}-t-lh`} label="Altura de línea" min={0.95} max={1.8} step={0.01} suffix="" value={tablet.lineHeight} onChange={lineHeight => setDevice('tablet', {
          lineHeight
        })} data-id="element-465" data-mp="32pibt1yuod9s" />
            <Slider id={`${block.id}-t-ls`} label="Espaciado entre letras" min={-2} max={6} step={0.5} value={tablet.letterSpacing} onChange={letterSpacing => setDevice('tablet', {
          letterSpacing
        })} data-id="element-466" data-mp="2yphyxlrega4u" />
            <Select id={`${block.id}-t-weight`} label="Grosor del título" placeholder="Automático" options={FONT_WEIGHTS} value={tablet.titleWeight ? String(tablet.titleWeight) : ''} onChange={e => setDevice('tablet', {
          titleWeight: e.target.value ? Number(e.target.value) : undefined
        })} data-id="element-467" data-mp="2ortu2302xm8y" />
            <Select id={`${block.id}-t-align`} label="Alineación" placeholder="Como en escritorio" options={[{
          value: 'left',
          label: 'Izquierda'
        }, {
          value: 'center',
          label: 'Centro'
        }, {
          value: 'right',
          label: 'Derecha'
        }]} value={tablet.align ?? ''} onChange={e => setDevice('tablet', {
          align: (e.target.value || undefined) as BlockStyle['align']
        })} data-id="element-468" data-mp="2f4zka1dv9sm1" />
            <Slider id={`${block.id}-t-py`} label="Margen vertical" min={0} max={120} step={2} value={tablet.paddingY} onChange={paddingY => setDevice('tablet', {
          paddingY
        })} data-id="element-469" data-mp="2svcsm4uo79bw" />
            <Slider id={`${block.id}-t-px`} label="Padding lateral" min={0} max={64} step={2} value={tablet.paddingX} onChange={paddingX => setDevice('tablet', {
          paddingX
        })} data-id="element-470" data-mp="37q9cjfbudl4t" />
          </div>}
      </section>

      {/* Ajustes que solo afectan a móvil (≤ 640 px). */}
      <section className="space-y-4 border-t border-border pt-5" data-id="element-471" data-mp="3sa386uec42xu">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-472" data-mp="3ndwa1ns6jvqg">
          Móvil
        </h4>
        <Switch id={`${block.id}-m-sync`} label="Usar los mismos valores en todos los dispositivos" checked={mobile.sync !== false} onChange={sync => setMobile({
        sync
      })} data-id="element-473" data-mp="3jdcdomffkw5d" />
        {mobile.sync === false && <div className="space-y-4 rounded-xl bg-surface p-4" data-id="element-474" data-mp="evgn5cmp2asr">
            <p className="text-xs text-muted-foreground" data-id="element-475" data-mp="aud5ckl364ow">
              Estos valores sustituyen solo la versión móvil. Escritorio y tablet
              no cambian.
            </p>
            <Slider id={`${block.id}-m-title`} label="Tamaño del título" min={8} max={96} onReset={() => setMobile({
          titleSize: undefined
        })} value={mobile.titleSize} onChange={titleSize => setMobile({
          titleSize
        })} data-id="element-476" data-mp="2vm6wo3xr7xth" />
            <Slider id={`${block.id}-m-body`} label="Tamaño del texto" min={8} max={64} onReset={() => setMobile({
          bodySize: undefined
        })} value={mobile.bodySize} onChange={bodySize => setMobile({
          bodySize
        })} data-id="element-477" data-mp="162yphi4kijid" />
            <Slider id={`${block.id}-m-lh`} label="Altura de línea" min={0.95} max={1.8} step={0.01} suffix="" value={mobile.lineHeight} onChange={lineHeight => setMobile({
          lineHeight
        })} data-id="element-478" data-mp="1jx8329b7dlwk" />
            <Slider id={`${block.id}-m-ls`} label="Espaciado entre letras" min={-2} max={6} step={0.5} value={mobile.letterSpacing} onChange={letterSpacing => setMobile({
          letterSpacing
        })} data-id="element-479" data-mp="1tt2qdhqdg55o" />
            <Select id={`${block.id}-m-weight`} label="Grosor del título" placeholder="Automático" options={FONT_WEIGHTS} value={mobile.titleWeight ? String(mobile.titleWeight) : ''} onChange={e => setMobile({
          titleWeight: e.target.value ? Number(e.target.value) : undefined
        })} data-id="element-480" data-mp="22uir0xrdwswp" />
            <Select id={`${block.id}-m-align`} label="Alineación" placeholder="Como en escritorio" options={[{
          value: 'left',
          label: 'Izquierda'
        }, {
          value: 'center',
          label: 'Centro'
        }, {
          value: 'right',
          label: 'Derecha'
        }]} value={mobile.align ?? ''} onChange={e => setMobile({
          align: (e.target.value || undefined) as BlockStyle['align']
        })} data-id="element-481" data-mp="1bsgsjkfi0ut3" />
            <Slider id={`${block.id}-m-py`} label="Margen vertical" min={0} max={96} step={2} value={mobile.paddingY} onChange={paddingY => setMobile({
          paddingY
        })} data-id="element-482" data-mp="1przolev1cfp1" />
            <Slider id={`${block.id}-m-px`} label="Padding lateral" min={0} max={40} step={2} value={mobile.paddingX} onChange={paddingX => setMobile({
          paddingX
        })} data-id="element-483" data-mp="24ifa6urd5pnq" />
          </div>}
      </section>

      <section className="space-y-4 border-t border-border pt-5" data-id="element-484" data-mp="28ihdt9gnst5n">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-485" data-mp="28g4mw6sxb8gg">
          Botón
        </h4>
        <Input id={`${block.id}-btn-label`} label="Texto del botón" placeholder="Usar el texto del contenido" value={button.label ?? ''} onChange={e => setButton({
        label: e.target.value
      })} data-id="element-486" data-mp="2x32jrpswld3t" />
        <div className="grid grid-cols-2 gap-3" data-id="element-487" data-mp="2n4mtfp1pa7vd">
          <Input id={`${block.id}-btn-color`} label="Color" type="color" value={button.color ?? '#3D8BFF'} onChange={e => setButton({
          color: e.target.value
        })} data-id="element-488" data-mp="2n3bovn0fkw37" />
          <Input id={`${block.id}-btn-hover`} label="Color hover" type="color" value={button.hoverColor ?? '#2F6FD1'} onChange={e => setButton({
          hoverColor: e.target.value
        })} data-id="element-489" data-mp="2e59sxlvk0tms" />
          <Input id={`${block.id}-btn-text`} label="Color del texto" type="color" value={button.textColor ?? '#FFFFFF'} onChange={e => setButton({
          textColor: e.target.value
        })} data-id="element-490" data-mp="24fgiwpb28mkn" />
          <Select id={`${block.id}-btn-icon`} label="Icono" options={BUTTON_ICONS} value={button.icon ?? ''} onChange={e => setButton({
          icon: e.target.value
        })} data-id="element-491" data-mp="1ve0agn3t2uj2" />
        </div>
        <Slider id={`${block.id}-btn-radius`} label="Radio de bordes" min={0} max={40} value={button.radius} onChange={radius => setButton({
        radius
      })} data-id="element-492" data-mp="1idoabwc9y8ao" />
        <Slider id={`${block.id}-btn-height`} label="Altura" min={36} max={80} value={button.height} onChange={height => setButton({
        height
      })} data-id="element-493" data-mp="19lmekbd5hg6q" />
        <Slider id={`${block.id}-btn-padding`} label="Padding horizontal" min={12} max={80} value={button.paddingX} onChange={paddingX => setButton({
        paddingX
      })} data-id="element-494" data-mp="14o4ezbl0wdbe" />
        <Select id={`${block.id}-btn-anim`} label="Animación" options={[{
        value: 'ninguna',
        label: 'Sin animación'
      }, {
        value: 'elevar',
        label: 'Elevar al pasar'
      }, {
        value: 'escalar',
        label: 'Escalar al pasar'
      }, {
        value: 'pulso',
        label: 'Pulso'
      }]} value={button.animation ?? 'elevar'} onChange={e => setButton({
        animation: e.target.value as BlockButtonStyle['animation']
      })} data-id="element-495" data-mp="upev5d9kgisi" />
        <Switch id={`${block.id}-btn-full`} label="Ancho completo" checked={Boolean(button.fullWidth)} onChange={fullWidth => setButton({
        fullWidth
      })} data-id="element-496" data-mp="brlcmq880lpf" />
        <Switch id={`${block.id}-btn-shadow`} label="Sombra y brillo" checked={button.shadow !== false} onChange={shadow => setButton({
        shadow
      })} data-id="element-497" data-mp="gl7j20nsjtcs" />
      </section>
    </div>;
}